import { memo, useState, useEffect } from "react";
import { Loader2, Sparkles, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const PL_PRODUITS = [["ca", "70 · Chiffre d'affaires"], ["autres_produits", "74/75 · Autres produits"]];
// Charges regroupées par palier de SIG (Soldes Intermédiaires de Gestion).
const G1 = [["achats", "60 · Achats / Matières premières"], ["loyers", "613 · Loyers"], ["autres_charges_externes", "61/62 · Autres achats et charges externes"]];
const G2 = [["autres_charges", "65 · Autres charges"], ["licenciements", "64 · Indemnités de licenciement"]];
const G3 = [["impots_taxes", "63 · Impôts et taxes"], ["salaires", "641 · Salaires et traitements"], ["charges_sociales", "645 · Charges sociales"]];
const G4 = [["amortissements", "68 · Amortissements"]];
// SUPER-LOT 104 — Charges financières (ligne 66) : alimentées par les intérêts d'emprunts, sous le résultat d'exploitation.
const G5 = [["charges_financieres", "66 · Charges financières"]];
export const PL_CHARGES = [...G1, ...G2, ...G3, ...G4];
// Postes alimentés automatiquement depuis les grilles de trésorerie (lecture seule sur les colonnes mensuelles).
const AUTO = new Set(["ca", "achats", "loyers", "autres_charges_externes", "impots_taxes", "autres_charges", "salaires", "charges_sociales", "charges_financieres"]);

const eur = (v) => (Number(v) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";
const num = (v) => Number(String(v ?? "").replace(/[^\d.-]/g, "")) || 0;
const pctLabel = (r) => (Number(r) * 100).toLocaleString("fr-FR", { maximumFractionDigits: 2 }) + " %";
const isMonth = (k) => k !== "n1" && k !== "trois_mois" && k !== "total";

// Saisie commit-on-blur pour éviter tout lag (état local, remonte au parent uniquement onBlur).
function CRInput({ value, onCommit, disabled, testId }) {
    const [local, setLocal] = useState(value ?? "");
    return (
        <input
            data-testid={testId}
            value={local}
            disabled={disabled}
            inputMode="numeric"
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => onCommit(num(local))}
            className="w-full min-w-[90px] rounded border border-slate-200 bg-white px-2 py-1 text-right tabular-nums text-sm focus:border-primary focus:outline-none disabled:bg-slate-50"
        />
    );
}

function CompteResultatGrid({ value, months = [], auto = {}, onChange, disabled = false, onPrefill, srcDocs = [], onComputed, showN1 = true, show3Mois = true }) {
    const [busy, setBusy] = useState("");
    const [n1Doc, setN1Doc] = useState("");
    const [troisDoc, setTroisDoc] = useState("");
    const [auditOpen, setAuditOpen] = useState(false);
    const cr = value || {};
    // SUPER-LOT 92 — ordre de lecture : [Mois prévisionnels] → [Rappel 3 mois précédents] → [Rappel exercice précédent].
    const cols = [...months, ...(months.length ? [{ key: "total", label: "Total Période" }] : []), ...(show3Mois ? [{ key: "trois_mois", label: "Rappel 3 mois précédents" }] : []), ...(showN1 ? [{ key: "n1", label: "Rappel exercice précédent" }] : [])];
    const firstMonthKey = months[0]?.key; // LOT 97 — mois M (partiel), visuellement distinct

    // ── SUPER-LOT 96 · Moteur de provision fiscale (ligne 63 uniquement) ───────────────────────────
    const fiscal = cr._fiscal || {};
    const setFiscal = (patch) => onChange({ ...cr, _fiscal: { ...fiscal, ...patch } });
    const n1v = (poste) => num((cr.n1 || {})[poste]);
    // Métriques d'intensité fiscale N-1 (auditables).
    const salN1 = n1v("salaires");
    const vaN1 = PL_PRODUITS.reduce((s, [p]) => s + n1v(p), 0) - G1.reduce((s, [p]) => s + n1v(p), 0) - G2.reduce((s, [p]) => s + n1v(p), 0);
    const impN1 = n1v("impots_taxes");
    const hasVal = (v) => v !== "" && v != null;
    const ratioSal = hasVal(fiscal.ratio_taxe_salaires) ? num(fiscal.ratio_taxe_salaires) / 100 : 0.02; // défaut 2 % des salaires
    const cfeMensuelle = hasVal(fiscal.cfe_mensuelle) ? num(fiscal.cfe_mensuelle) : 0;
    // Solde des taxes N-1 après impôt sur salaires & CFE annuelle → rapporté à la VA (assiette CVAE).
    const residualN1 = Math.max(0, impN1 - ratioSal * salN1 - cfeMensuelle * 12);
    const ratioVaDefaut = vaN1 > 0 ? residualN1 / vaN1 : 0;
    const ratioVa = hasVal(fiscal.ratio_cvae) ? num(fiscal.ratio_cvae) / 100 : ratioVaDefaut;
    const hasOverride = hasVal(fiscal.override_global);

    const get = (period, poste) => {
        if (period === "total") return months.reduce((s, m) => s + num(get(m.key, poste)), 0); // LOT 99 — somme horizontale des mois projetés
        if (isMonth(period) && poste === "impots_taxes") return fiscalParts(period).total; // provision calculée
        return (isMonth(period) && AUTO.has(poste)) ? (auto[period]?.[poste] ?? 0) : (cr[period] || {})[poste];
    };
    const setCell = (period, poste, v) => onChange({ ...cr, [period]: { ...(cr[period] || {}), [poste]: v } });
    const sumP = (k) => PL_PRODUITS.reduce((s, [p]) => s + num(get(k, p)), 0);
    const sumG = (k, g) => g.reduce((s, [p]) => s + num(get(k, p)), 0);
    const mb = (k) => sumP(k) - sumG(k, G1);
    const va = (k) => mb(k) - sumG(k, G2);
    const ebe = (k) => va(k) - sumG(k, G3);
    const re = (k) => ebe(k) - sumG(k, G4);
    const rc = (k) => re(k) - sumG(k, G5); // SUPER-LOT 104 — Résultat courant avant impôt = Résultat d'exploitation − charges financières (66)

    // Décomposition auditable de la provision « 63 » d'un mois prévisionnel. Priorité : override > saisie 63 > calcul auto.
    function fiscalParts(period) {
        const sal = num(auto[period]?.salaires ?? 0);
        const vaM = va(period);
        const taxeSal = sal * ratioSal;
        const taxeVa = Math.max(0, vaM) * ratioVa; // une taxe assise sur la VA ne peut pas être négative
        const auto63 = num(auto[period]?.impots_taxes ?? 0); // charges compte 63 saisies dans les grilles
        const calc = Math.max(0, Math.round(taxeSal + taxeVa + cfeMensuelle));
        let total, mode;
        if (hasOverride) { total = Math.round(num(fiscal.override_global)); mode = "override"; }
        else if (auto63 > 0) { total = Math.round(auto63); mode = "manual"; }
        else { total = calc; mode = "auto"; }
        return { sal, vaM, taxeSal, taxeVa, cfe: cfeMensuelle, auto63, calc, total, mode };
    }

    // HOTFIX 105 — Source Unique de Vérité : on émet le P&L mensuel DÉJÀ calculé (dont la ligne 63 du moteur fiscal)
    // vers le parent, qui le persiste dans data_json.pnl_snapshot. Le PDF se contentera de lire ces valeurs brutes.
    const _posteKeys = [...PL_PRODUITS, ...PL_CHARGES, ...G5].map(([p]) => p);
    const pnlSnapshot = {};
    for (const m of months) {
        const row = {};
        for (const p of _posteKeys) row[p] = num(get(m.key, p));
        pnlSnapshot[m.key] = row;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { onComputed?.(pnlSnapshot); }, [JSON.stringify(pnlSnapshot)]);

    async function prefill(period, docId) {
        if (!onPrefill) return;
        setBusy(period);
        try { await onPrefill(period, docId); toast.success(period === "n1" ? "Compte de résultat N-1 pré-rempli" : "Rappel 3 mois précédents pré-rempli"); }
        catch (e) { toast.error("Pré-remplissage impossible", { description: e.message }); }
        finally { setBusy(""); }
    }

    const Row = ({ poste, label }) => (
        <tr data-testid={`cr-row-${poste}`} className="border-t border-slate-100">
            <td className="sticky left-0 z-10 min-w-[220px] bg-white px-3 py-1.5 text-left">
                <span className="inline-flex items-center gap-1.5">
                    {label}
                    {poste === "impots_taxes" && (
                        <button type="button" data-testid="cr-fiscal-audit-open" onClick={() => setAuditOpen(true)} title="Détail & contrôle de la provision fiscale" className="inline-flex items-center rounded-full border border-indigo-200 p-1 text-primary hover:bg-indigo-50">
                            <SlidersHorizontal className="h-3.5 w-3.5" />
                        </button>
                    )}
                </span>
            </td>
            {cols.map((c) => {
                const ro = isMonth(c.key) && AUTO.has(poste);
                const first = isMonth(c.key) && c.key === firstMonthKey;
                const isTotal = c.key === "total";
                const title = poste === "impots_taxes" && isMonth(c.key) ? "Provision fiscale calculée (cliquer sur l'icône ⚙ pour auditer)" : "Alimenté automatiquement depuis les grilles (Montant HT)";
                if (isTotal) {
                    return (
                        <td key={c.key} className="px-2 py-1 text-right tabular-nums bg-indigo-50/60">
                            <span data-testid={`cr-${poste}-total`} title="Total de la période projetée (somme des mois M à M+x)" className="block w-full min-w-[90px] rounded px-2 py-1 text-right tabular-nums text-sm font-semibold text-slate-800">{eur(get("total", poste))}</span>
                        </td>
                    );
                }
                return (
                    <td key={c.key} className={`px-2 py-1 text-right tabular-nums${first ? " bg-slate-50" : ""}`}>
                        {ro
                            ? <span data-testid={`cr-${poste}-${c.key}`} title={title} className={`block w-full min-w-[90px] rounded px-2 py-1 text-right tabular-nums text-sm ${first ? "bg-slate-200 text-slate-600" : "bg-slate-100 text-slate-500"}`}>{eur(get(c.key, poste))}</span>
                            : <CRInput testId={`cr-${poste}-${c.key}`} value={get(c.key, poste)} disabled={disabled} onCommit={(v) => setCell(c.key, poste, v)} />}
                    </td>
                );
            })}
        </tr>
    );

    const SigRow = ({ id, label, comp, dark }) => (
        <tr data-testid={`cr-sig-${id}`} className={dark ? "border-t-2 border-slate-800 bg-slate-800 font-semibold text-white" : "border-t border-slate-200 bg-indigo-50 font-semibold"}>
            <td className={`sticky left-0 z-10 min-w-[220px] px-3 py-2 text-left ${dark ? "bg-slate-800" : "bg-indigo-50"}`}>{label}</td>
            {cols.map((c) => {
                const v = comp(c.key);
                const cls = dark ? (v < 0 ? "text-rose-300" : "text-emerald-300") : "text-slate-900";
                const first = isMonth(c.key) && c.key === firstMonthKey;
                const isTotal = c.key === "total";
                const bg = isTotal ? (dark ? " bg-indigo-950" : " bg-indigo-100") : (first ? (dark ? " bg-slate-900" : " bg-indigo-100") : "");
                return <td key={c.key} data-testid={`cr-${id}-${c.key}`} className={`px-2 py-2 text-right tabular-nums ${cls}${bg}${isTotal ? " font-bold" : ""}`}>{eur(v)}</td>;
            })}
        </tr>
    );

    const MODE_BADGE = {
        override: ["Forcé", "bg-rose-100 text-rose-700"],
        manual: ["Saisie 63", "bg-amber-100 text-amber-700"],
        auto: ["Auto", "bg-indigo-100 text-indigo-700"],
    };

    return (
        <div data-testid="compte-resultat-grid" className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h3 className="text-base font-semibold text-slate-900">Compte de Résultat Prévisionnel</h3>
                    <p className="text-xs text-muted-foreground">Colonnes mensuelles alimentées automatiquement (Montant HT) depuis les grilles · Soldes Intermédiaires de Gestion calculés · Rappels N-1 &amp; 3 mois saisissables.</p>
                </div>
                {onPrefill && (
                    <div className="flex flex-wrap items-center gap-3">
                        {showN1 && (
                        <div className="flex items-center gap-1.5">
                            <select data-testid="cr-n1-source" value={n1Doc} onChange={(e) => setN1Doc(e.target.value)} disabled={disabled || !!busy} className="h-8 max-w-[190px] rounded border border-slate-200 bg-white px-2 text-xs">
                                <option value="">Data Warehouse (auto)</option>
                                {srcDocs.map((d) => <option key={d.id} value={d.id}>{d.nom_fichier}</option>)}
                            </select>
                            <button type="button" data-testid="cr-prefill-n1" disabled={disabled || !!busy} onClick={() => prefill("n1", n1Doc)} className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 px-3 py-1.5 text-xs text-primary hover:bg-indigo-50 disabled:opacity-50">
                                {busy === "n1" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {busy === "n1" ? "Extraction N-1…" : "Pré-remplir N-1"}
                            </button>
                        </div>
                        )}
                        {show3Mois && (
                        <div className="flex items-center gap-1.5">
                            <select data-testid="cr-trois-source" value={troisDoc} onChange={(e) => setTroisDoc(e.target.value)} disabled={disabled || !!busy} className="h-8 max-w-[190px] rounded border border-slate-200 bg-white px-2 text-xs">
                                <option value="">— Fichier source —</option>
                                {srcDocs.map((d) => <option key={d.id} value={d.id}>{d.nom_fichier}</option>)}
                            </select>
                            <button type="button" data-testid="cr-prefill-trois" disabled={disabled || !!busy || !troisDoc} onClick={() => prefill("trois_mois", troisDoc)} className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 px-3 py-1.5 text-xs text-primary hover:bg-indigo-50 disabled:opacity-50">
                                {busy === "trois_mois" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {busy === "trois_mois" ? "Extraction 3 mois…" : "Pré-remplir 3 mois précédents"}
                            </button>
                        </div>
                        )}
                    </div>
                )}
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full border-collapse text-sm">
                    <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                        <tr>
                            <th className="sticky left-0 z-20 min-w-[220px] bg-slate-100 px-3 py-2 text-left">Poste</th>
                            {cols.map((c) => { const first = isMonth(c.key) && c.key === firstMonthKey; const isTotal = c.key === "total"; return <th key={c.key} title={first ? "Mois partiel à compter de la date de démarrage" : (isTotal ? "Total de la période projetée (somme des mois)" : undefined)} className={`min-w-[100px] px-2 py-2 text-right${first ? " bg-slate-200 text-slate-700" : ""}${isTotal ? " bg-indigo-100 text-indigo-900 font-semibold" : ""}`}>{first ? "M (*)" : c.label}{c.sub && <span className="block text-[10px] font-normal normal-case text-slate-400">{c.sub}</span>}</th>; })}
                        </tr>
                    </thead>
                    <tbody>
                        {PL_PRODUITS.map(([p, l]) => <Row key={p} poste={p} label={l} />)}
                        <SigRow id="total-produits" label="Total Produits d'exploitation (T1)" comp={sumP} />
                        {G1.map(([p, l]) => <Row key={p} poste={p} label={l} />)}
                        <SigRow id="marge-brute" label="Marge brute" comp={mb} />
                        {G2.map(([p, l]) => <Row key={p} poste={p} label={l} />)}
                        <SigRow id="valeur-ajoutee" label="Valeur Ajoutée (VA)" comp={va} />
                        {G3.map(([p, l]) => <Row key={p} poste={p} label={l} />)}
                        <SigRow id="ebe" label="Excédent Brut d'Exploitation (EBE)" comp={ebe} />
                        {G4.map(([p, l]) => <Row key={p} poste={p} label={l} />)}
                        <SigRow id="resultat" label="Résultat d'exploitation" comp={re} dark />
                        {G5.map(([p, l]) => <Row key={p} poste={p} label={l} />)}
                        <SigRow id="resultat-courant" label="Résultat courant avant impôt" comp={rc} />
                    </tbody>
                </table>
            </div>

            {/* SUPER-LOT 96 — Panneau d'audit de la provision fiscale (ligne 63) */}
            <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
                <DialogContent data-testid="cr-fiscal-audit-modal" className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Détail de la provision fiscale — 63 · Impôts et taxes</DialogTitle>
                        <DialogDescription>Provision calculée à partir des ratios N-1 et des variables d'exploitation prévisionnelles. L'experte peut ajuster chaque paramètre ou forcer un montant mensuel global.</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 text-sm">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Base de calcul déduite du N-1</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                                <div>Impôts &amp; taxes N-1 : <span className="font-semibold" data-testid="cr-fiscal-n1-imp">{eur(impN1)}</span></div>
                                <div>Salaires 641 N-1 : <span className="font-semibold">{eur(salN1)}</span></div>
                                <div>Valeur Ajoutée N-1 : <span className="font-semibold">{eur(vaN1)}</span></div>
                                <div>Taux sur salaires : <span className="font-semibold text-primary">{pctLabel(ratioSal)}</span></div>
                                <div>Taux sur VA (CVAE) : <span className="font-semibold text-primary">{pctLabel(ratioVa)}</span></div>
                                <div>CFE / foncier mensuel : <span className="font-semibold text-primary">{eur(cfeMensuelle)}</span></div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <label className="flex flex-col gap-1 text-xs text-slate-600">Taux sur salaires (%)
                                <input data-testid="cr-fiscal-ratio-sal" type="text" inputMode="decimal" disabled={disabled} defaultValue={fiscal.ratio_taxe_salaires ?? ""} placeholder="2 (défaut)" onBlur={(e) => setFiscal({ ratio_taxe_salaires: e.target.value.trim() })} className="rounded border border-slate-200 px-2 py-1.5 text-right tabular-nums focus:border-primary focus:outline-none" />
                            </label>
                            <label className="flex flex-col gap-1 text-xs text-slate-600">Taux sur VA / CVAE (%)
                                <input data-testid="cr-fiscal-ratio-va" type="text" inputMode="decimal" disabled={disabled} defaultValue={fiscal.ratio_cvae ?? ""} placeholder={`${pctLabel(ratioVaDefaut).replace(" %", "")} (défaut N-1)`} onBlur={(e) => setFiscal({ ratio_cvae: e.target.value.trim() })} className="rounded border border-slate-200 px-2 py-1.5 text-right tabular-nums focus:border-primary focus:outline-none" />
                            </label>
                            <label className="flex flex-col gap-1 text-xs text-slate-600">CFE / foncier mensuel (€)
                                <input data-testid="cr-fiscal-cfe" type="text" inputMode="decimal" disabled={disabled} defaultValue={fiscal.cfe_mensuelle ?? ""} placeholder="0" onBlur={(e) => setFiscal({ cfe_mensuelle: e.target.value.trim() })} className="rounded border border-slate-200 px-2 py-1.5 text-right tabular-nums focus:border-primary focus:outline-none" />
                            </label>
                        </div>

                        <label className="flex flex-col gap-1 rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-xs font-medium text-rose-800">
                            Forcer le montant mensuel global (€) — écrase l'algorithme pour tous les mois
                            <input data-testid="cr-fiscal-override" type="text" inputMode="decimal" disabled={disabled} defaultValue={fiscal.override_global ?? ""} placeholder="Laisser vide = calcul automatique" onBlur={(e) => setFiscal({ override_global: e.target.value.trim() })} className="mt-1 w-full rounded border border-rose-300 bg-white px-2 py-1.5 text-right tabular-nums text-slate-900 focus:border-rose-500 focus:outline-none" />
                        </label>

                        <div className="overflow-x-auto rounded-lg border border-slate-200">
                            <table className="w-full border-collapse text-xs">
                                <thead className="bg-slate-100 uppercase tracking-wide text-slate-500">
                                    <tr>
                                        <th className="px-2 py-1.5 text-left">Mois</th>
                                        <th className="px-2 py-1.5 text-right">Salaires ×{pctLabel(ratioSal)}</th>
                                        <th className="px-2 py-1.5 text-right">VA ×{pctLabel(ratioVa)}</th>
                                        <th className="px-2 py-1.5 text-right">CFE fixe</th>
                                        <th className="px-2 py-1.5 text-right">Total mois</th>
                                        <th className="px-2 py-1.5 text-center">Mode</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {months.map((m) => {
                                        const f = fiscalParts(m.key);
                                        const [badge, cls] = MODE_BADGE[f.mode];
                                        return (
                                            <tr key={m.key} data-testid={`cr-fiscal-row-${m.key}`} className="border-t border-slate-100 tabular-nums">
                                                <td className="px-2 py-1.5 text-left font-medium">{m.label} <span className="text-slate-400">{m.sub}</span></td>
                                                <td className="px-2 py-1.5 text-right">{eur(f.taxeSal)}</td>
                                                <td className="px-2 py-1.5 text-right">{eur(f.taxeVa)}</td>
                                                <td className="px-2 py-1.5 text-right">{eur(f.cfe)}</td>
                                                <td className="px-2 py-1.5 text-right font-semibold" data-testid={`cr-fiscal-total-${m.key}`}>{eur(f.total)}</td>
                                                <td className="px-2 py-1.5 text-center"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{badge}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Formule : <span className="font-medium">(Salaires du mois × taux salaires) + (VA du mois × taux VA) + CFE fixe</span>. Une charge « 63… » saisie manuellement dans les grilles prime sur le calcul. Un montant global forcé écrase tout.</p>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default memo(CompteResultatGrid);
